/**
 * Fixtures Page - Main container for the Fixtures feature
 * Refactored to use custom hooks and modular components
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";

import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import ClubbieModal from "../../components/Modal/ClubbieModal";
import {
  FilterPanel,
  EditFixtureModal,
  AddResultModal,
  DeleteFixtureModal,
  GenerateFixturesModal,
} from "../../components/Fixtures";
import CreateFixtureModal from "../../components/CreateFixtureModal/CreateFixtureModal";
import SwapTeamsModal from "../../components/SwapTeamsModal/SwapTeamsModal";
import CategoryTabs from "../tournament/format/components/CategoryTabs";
import { useIsPublic } from "../../context/PublicContext";
import { getSelectedTournament, selectCategory, goLive, fetchTournaments, fetchUndoableSwap, undoLastSwap } from "../../redux/slices/tournamentSlice";
import useTournamentRole from "../../hooks/useTournamentRole";

import { VIEW_MODES } from "./constants";
import FixtureCalendarView from "./FixtureCalendarView";
import FixtureListView from "./FixtureListView";
import { useFixtureState } from "./hooks";

/**
 * Fixtures - Main fixture management page
 * Displays fixtures in list or calendar view with filtering and CRUD operations
 */
const Fixtures = () => {
  const dispatch = useDispatch();
  const isPublic = useIsPublic();
  const [isGoingLive, setIsGoingLive] = useState(false);
  const [isConfirmGoLiveOpen, setIsConfirmGoLiveOpen] = useState(false);
  const [isSwapTeamsOpen, setIsSwapTeamsOpen] = useState(false);
  const [isConfirmUndoOpen, setIsConfirmUndoOpen] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);

  const { role, permissions } = useTournamentRole();

  // Get selected organisation from global state for reliable refreshing
  const { selectedOrganisation } = useSelector((state) => state.organisation);
  const undoableSwap = useSelector((state) => state.tournament.undoableSwap);

  const {
    // Redux state
    selectedTournament,

    // View state
    viewMode,
    setViewMode,
    startDate,
    endDate,
    handleDateChange,
    handleShiftDates,

    // Filter state
    filters,
    showConflicts,
    setShowConflicts,
    isFilterPanelOpen,
    openFilterPanel,
    closeFilterPanel,
    filterOptions,
    activeFiltersCount,

    // Filter handlers
    handlePhaseFilterChange,
    handleBracketFilterChange,
    handlePitchFilterChange,
    handleTeamFilterChange,
    clearAllFilters,

    // Fixture data
    allFixtures,
    fixturesByDate,
    sortedDates,

    // Modal states
    isCreateModalOpen,
    isEditModalOpen,
    isDeleteModalOpen,
    isGenerateModalOpen,
    isAddResultModalOpen,
    selectedFixture,
    selectedResult,

    // Modal handlers
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openDeleteModal,
    closeDeleteModal,
    openGenerateModal,
    closeGenerateModal,
    openAddResultModal,
    openEditResultModal,
    closeAddResultModal,
    selectedCategory,
  } = useFixtureState();

  // Get categories from tournament
  const tournamentKey = selectedTournament?.id || selectedTournament?._id;
  const categories = useMemo(() => selectedTournament?.categories || [], [tournamentKey]);

  // Build set of locked phase IDs for gating result buttons
  const lockedPhaseIds = useMemo(() => {
    const phases = selectedCategory?.phases || [];
    return new Set(phases.filter((p) => p.locked).map((p) => p._id || p.id));
  }, [selectedCategory]);

  // Auto-select first category when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      dispatch(selectCategory(categories[0]));
    }
  }, [categories, selectedCategory, dispatch]);

  const handleCategoryChange = (label) => {
    const category = categories.find(c => c.label === label);
    if (category) {
      dispatch(selectCategory(category));
    }
  };

  useEffect(() => {
    if (isPublic) return;
    if (!selectedOrganisation?.id) return;
    dispatch(getSelectedTournament(selectedOrganisation.id));
  }, [dispatch, selectedOrganisation?.id, isPublic]);

  // Handle view toggle
  const handleToggleView = () => {
    setViewMode((prev) =>
      prev === VIEW_MODES.LIST ? VIEW_MODES.CALENDAR : VIEW_MODES.LIST
    );
  };

  // Refresh whether the latest swap in this category is still undoable. Undo
  // lapses once a result is added, so we re-check after any fixture change.
  const tournamentId = selectedTournament?.id || selectedTournament?._id;
  const categoryId = selectedCategory?._id || selectedCategory?.id;
  const refreshUndoableSwap = useCallback(() => {
    if (isPublic || !permissions.canManageFixtures) return;
    if (!tournamentId || !categoryId) return;
    dispatch(fetchUndoableSwap({ tournamentId, categoryId }));
  }, [dispatch, isPublic, permissions.canManageFixtures, tournamentId, categoryId]);

  useEffect(() => {
    refreshUndoableSwap();
  }, [refreshUndoableSwap]);

  const handleFixtureSuccess = () => {
    if (selectedOrganisation?.id) {
      dispatch(getSelectedTournament(selectedOrganisation.id));
    }
    refreshUndoableSwap();
  };

  const handleConfirmUndo = async () => {
    if (!tournamentId || !categoryId) return;
    try {
      setIsUndoing(true);
      const loadingToast = toast.loading("Undoing team replacement...");
      await dispatch(undoLastSwap({ tournamentId, categoryId })).unwrap();
      if (selectedOrganisation?.id) {
        await dispatch(getSelectedTournament(selectedOrganisation.id));
      }
      toast.dismiss(loadingToast);
      toast.success("Team replacement undone");
    } catch (error) {
      toast.error(error?.message || error || "Failed to undo replacement");
    } finally {
      setIsUndoing(false);
      setIsConfirmUndoOpen(false);
    }
  };

  const handleConfirmGoLive = async () => {
    try {
      setIsGoingLive(true);
      const loadingToast = toast.loading("Going live...");
      await dispatch(goLive({ tournamentId: selectedTournament.id, categoryId: selectedCategory._id })).unwrap();
      await dispatch(fetchTournaments({ organisationId: selectedOrganisation.id })).unwrap();
      toast.dismiss(loadingToast);
      toast.success("Competition is now live!");
    } catch (error) {
      toast.error(error.message || "Failed to go live");
    } finally {
      setIsGoingLive(false);
      setIsConfirmGoLiveOpen(false);
    }
  };

  // Check if we're in calendar view
  const isCalendarView = viewMode === VIEW_MODES.CALENDAR;

  const goLiveFooter = (
    <div className="flex justify-end gap-3 w-full">
      <button
        type="button"
        className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
        onClick={() => setIsConfirmGoLiveOpen(false)}
      >
        Cancel
      </button>
      <button
        type="button"
        className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        onClick={handleConfirmGoLive}
        disabled={isGoingLive}
      >
        {isGoingLive ? <><LoadingSpinner size="small" /><span>Publishing...</span></> : "Publish"}
      </button>
    </div>
  );

  const undoSwapFooter = (
    <div className="flex justify-end gap-3 w-full">
      <button
        type="button"
        className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
        onClick={() => setIsConfirmUndoOpen(false)}
      >
        Cancel
      </button>
      <button
        type="button"
        className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        onClick={handleConfirmUndo}
        disabled={isUndoing}
      >
        {isUndoing ? <><LoadingSpinner size="small" /><span>Undoing...</span></> : "Undo replacement"}
      </button>
    </div>
  );

  const isEmpty = sortedDates.length === 0;

  return (
    <div className={`bg-[#000F1F] ${isEmpty ? "h-full flex flex-col" : "min-h-screen"}`}>
      <div className={`p-3 sm:p-[18px] lg:px-8 flex flex-col gap-5 ${isEmpty ? "flex-1 min-h-0" : ""}`}>
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h1 className="text-[22px] font-normal text-[#FEFCEB]">Fixtures</h1>
          {!isPublic && permissions.canManageFixtures && (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={openCreateModal}
                className="px-4 py-2 text-[15px] font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
              >
                Create fixture
              </button>
              <button
                onClick={openGenerateModal}
                className="bg-[#00E7FF] hover:opacity-50 text-[#021529] text-[15px] font-medium px-4 py-2 rounded-full transition-all active:scale-95"
              >
                Generate fixtures
              </button>
            </div>
          )}
        </div>

        {/* Category Tabs + Go Live */}
        {categories.length > 0 && (
          <div className="flex items-center justify-between">
            <CategoryTabs
              categories={categories}
              selectedCategory={selectedCategory?.label}
              onCategoryChange={handleCategoryChange}
            />
            {!isPublic && permissions.canGoLive && (
              <button
                className="px-4 py-2 text-[15px] font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
                onClick={() => setIsConfirmGoLiveOpen(true)}
                disabled={isGoingLive}
              >
                Publish fixtures
              </button>
            )}
          </div>
        )}

      {/* Main Content */}
      {
        isCalendarView ? (
          <FixtureCalendarView
            fixturesByDate={fixturesByDate}
            sortedDates={sortedDates}
            showConflicts={showConflicts}
            selectedTournament={selectedTournament}
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            onShiftDates={handleShiftDates}
            isCalendarView={isCalendarView}
            onToggleView={handleToggleView}
            onOpenFilters={openFilterPanel}
            onOpenSwapTeams={() => setIsSwapTeamsOpen(true)}
            onUndoSwap={() => setIsConfirmUndoOpen(true)}
            undoableSwap={undoableSwap}
            activeFiltersCount={activeFiltersCount}
            onGenerateFixtures={openGenerateModal}
            onCreateFixture={openCreateModal}
            isReadOnly={isPublic || !permissions.canManageFixtures}
            canManageFixtures={permissions.canManageFixtures}
            canAddResults={false}
            lockedPhaseIds={lockedPhaseIds}
            isOwner={role === "owner"}
            onEditFixture={openEditModal}
            onDeleteFixture={openDeleteModal}
            onAddResult={openAddResultModal}
            onEditResult={openEditResultModal}
          />
        ) : (
          <FixtureListView
            fixturesByDate={fixturesByDate}
            sortedDates={sortedDates}
            showConflicts={showConflicts}
            setShowConflicts={setShowConflicts}
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            onShiftDates={handleShiftDates}
            isCalendarView={isCalendarView}
            onToggleView={handleToggleView}
            onOpenFilters={openFilterPanel}
            onOpenSwapTeams={() => setIsSwapTeamsOpen(true)}
            onUndoSwap={() => setIsConfirmUndoOpen(true)}
            undoableSwap={undoableSwap}
            activeFiltersCount={activeFiltersCount}
            selectedTournament={selectedTournament}
            onEditFixture={openEditModal}
            onDeleteFixture={openDeleteModal}
            onAddResult={openAddResultModal}
            onEditResult={openEditResultModal}
            onGenerateFixtures={openGenerateModal}
            onCreateFixture={openCreateModal}
            categories={categories}
            selectedCategory={selectedCategory?.label}
            onCategoryChange={handleCategoryChange}
            isReadOnly={isPublic || !permissions.canManageFixtures}
            canManageFixtures={permissions.canManageFixtures}
            canAddResults={false}
            lockedPhaseIds={lockedPhaseIds}
            isOwner={role === "owner"}
          />
        )
      }
      </div>

      {/* Filter Panel */}
      <FilterPanel
        isOpen={isFilterPanelOpen}
        onClose={closeFilterPanel}
        showConflicts={showConflicts}
        setShowConflicts={setShowConflicts}
        phases={filterOptions.phases}
        selectedPhases={filters.phases}
        onPhaseChange={handlePhaseFilterChange}
        brackets={filterOptions.brackets}
        selectedBrackets={filters.brackets}
        onBracketChange={handleBracketFilterChange}
        pitches={filterOptions.pitches}
        selectedPitches={filters.pitches}
        onPitchChange={handlePitchFilterChange}
        teams={filterOptions.teams}
        selectedTeams={filters.teams}
        onTeamChange={handleTeamFilterChange}
        onClearAll={clearAllFilters}
        onApply={closeFilterPanel}
      />

      {!isPublic && (
        <>
          {/* Create Fixture Modal */}
          <CreateFixtureModal
            isOpen={isCreateModalOpen}
            onClose={closeCreateModal}
            onSuccess={handleFixtureSuccess}
          />

          {/* Edit Fixture Modal */}
          <EditFixtureModal
            isOpen={isEditModalOpen}
            onClose={closeEditModal}
            fixture={selectedFixture}
            onSuccess={handleFixtureSuccess}
          />

          {/* Delete Fixture Modal */}
          <DeleteFixtureModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            fixture={selectedFixture}
            onSuccess={handleFixtureSuccess}
          />

          {/* Generate Fixtures Modal */}
          <GenerateFixturesModal
            isOpen={isGenerateModalOpen}
            onClose={closeGenerateModal}
            onSuccess={handleFixtureSuccess}
          />

          {/* Swap Teams Modal */}
          <SwapTeamsModal
            isOpen={isSwapTeamsOpen}
            onClose={() => setIsSwapTeamsOpen(false)}
            onSuccess={handleFixtureSuccess}
            fixtures={allFixtures}
          />

          {/* Add/Edit Result Modal */}
          <AddResultModal
            isOpen={isAddResultModalOpen}
            onClose={closeAddResultModal}
            fixture={selectedFixture}
            result={selectedResult}
            type={selectedResult ? "edit" : "add"}
            onSuccess={handleFixtureSuccess}
          />

          {/* Go Live Confirm Modal */}
          <ClubbieModal
            isOpen={isConfirmGoLiveOpen}
            width="500px"
            toggle={() => setIsConfirmGoLiveOpen(false)}
            title="Publish fixtures"
            footer={goLiveFooter}
          >
            <p className="text-app-text-secondary text-[13px]">
              Are you sure you want to publish the fixtures? This will create events that actual users will be able to see on their devices. This action cannot be undone.
            </p>
          </ClubbieModal>

          {/* Undo Team Replacement Confirm Modal */}
          <ClubbieModal
            isOpen={isConfirmUndoOpen}
            width="500px"
            toggle={() => setIsConfirmUndoOpen(false)}
            title="Undo team replacement"
            footer={undoSwapFooter}
          >
            <p className="text-app-text-secondary text-[13px]">
              {undoableSwap?.swap
                ? `This reverts the most recent replacement (${undoableSwap.swap.incomingTeamName} → ${undoableSwap.swap.outgoingTeamName}) and restores fixtures, results and standings to how they were just before it. Once a result is added you can no longer undo.`
                : "This reverts the most recent team replacement and restores fixtures, results and standings to how they were just before it. Once a result is added you can no longer undo."}
            </p>
          </ClubbieModal>
        </>
      )}
    </div>
  );
};

export default Fixtures;
